<template>
  <section
    class="PlayerList"
    :class="{
      'PlayerList--isActive': isActive,
    }"
  >
    <header class="PlayerList__header">
      <h3 class="PlayerList__headerTitle">
        <Icon
          v-if="iconName"
          class="PlayerList__headerIcon"
          size="medium"
          :name="iconName"
        /> <span class="PlayerList__headerTitleLabel">{{ title }}</span>
      </h3>
      <h4
        v-if="subtitle"
        class="PlayerList__headerSubtitle"
      >
        {{ subtitle }}
      </h4>
    </header>

    <ol
      v-if="players.length > 0"
      ref="listRef"
      class="PlayerList__list"
    >
      <PlayerListItem
        v-for="player in players"
        :key="player.id"
        v-bind="{player, isClickable}"
        :status="statusMap.get(player.id)"
        :subRequest="subRequestMap.get(player.id)"
        :award="awardMap?.get(player.id)"
        @click:player="handlePlayerClick"
        @click:supporterBadge="handleSupporterStatusClick"
      >
        <template
          v-if="$slots.buttonList"
          #buttonList
        >
          <slot
            name="buttonList"
            v-bind="{player}"
            :status="((statusMap.get(player.id) || null) as Status | null)"
          />
        </template>
      </PlayerListItem>
    </ol>
    <slot
      v-if="players.length === 0"
      name="emptyState"
    />
  </section>
</template>

<script lang="ts" setup>
import Icon from "@/components/Icon.vue";
import { RouteName } from "@/lib/router/routes";
import getRouteForPlayer from "@/lib/utils/route/getRouteForPlayer";
import useAuthStore from "@/stores/useAuthStore";
import useModalStore from "@/stores/useModalStore";
import { Award, PlayerSummary, Status, SubRequestWithAssociations } from "@shared/lib/database/query.types";
import { storeToRefs } from "pinia";
import { useTemplateRef } from "vue";
import { IconName } from "./Icon.types";
import PlayerListItem from "./PlayerListItem.vue";

const {
  statusMap = new Map(),
  subRequestMap = new Map(),
  isClickable = true,
} = defineProps<{
  iconName?: keyof typeof IconName,
  title: string,
  subtitle?: string,
  players: Array<PlayerSummary>,
  statusMap?: Map<string, Status>,
  subRequestMap?: Map<string, SubRequestWithAssociations>,
  awardMap?: Map<string, Award>,
  isActive?: boolean,
  maxCount?: number,
  isClickable?: boolean,
}>();

defineExpose({
  scrollToBottom,
});

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

const {
  openModal,
} = useModalStore();

const listRef = useTemplateRef<HTMLOListElement>("listRef");

function scrollToBottom () {
  if (listRef.value) {
    listRef.value.scrollTo({
      behavior: "smooth",
      top: listRef.value.scrollHeight,
    });
  }
}

function handlePlayerClick (player: PlayerSummary) {
  if (!isClickable) return;

  openModal(getRouteForPlayer(player.id));
}

function handleSupporterStatusClick () {
  if (currentPlayer.value?.supporter_type) return;

  openModal({
    name: RouteName.supporter,
  });
}
</script>

<style lang="scss" scoped>
.PlayerList {
  --itemHeight: 48px;
  position: relative;

  display: grid;
  gap: 2px;
  align-items: start;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: 8px;
  background-color: var(--themeColorWhite);
  color: var(--themeColorNeutral900);

  &:has(.PlayerList__list) {
    align-content: start;
  }

  &:not(:has(.PlayerList__list)) {
    grid-template-rows: min-content auto;
  }
}

.PlayerList__header {
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 7px;
  justify-content: center;
  height: 44px;
  padding: 0 16px 0 12px;

  border-radius: 6px 6px 0 0;
  background-color: var(--themeColorNeutral100);

  .PlayerList--isActive & {
    background-color: var(--themeColorSecondary);
    color: var(--themeColorSecondaryForeground);
  }

  &:has(.PlayerList__headerSubtitle) {
    height: 64px;
  }

  &::after {
    position: absolute;
    top: 100%;
    left: 0;

    width: 100%;

    border-bottom: solid 2px var(--themeColorNeutral900);

    content: "";
  }
}

.PlayerList__headerTitle {
  display: flex;
  gap: 5px;
  align-items: center;

  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  text-transform: uppercase;
}

.PlayerList__headerTitleLabel {
  position: relative;
  top: 1px;
}

.PlayerList__headerSubtitle {
  grid-column: span 2;
  grid-row: 2;

  opacity: 0.6;

  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);

  .PlayerList--isActive & {
    color: var(--themeColorSecondaryForeground);
  }
}

.PlayerList__list {
  --maxHeight: calc((var(--itemHeight) + 1px) * (v-bind(maxCount) + 0.5));
  display: grid;
  gap: 1px;
  max-height: var(--maxHeight, 100%);
  overflow-y: auto;

  border-radius: 0 0 6px 6px;
}
</style>
